/**
 * CSS for the standalone (full-page) survey layout.
 * All dynamic values come from CSS custom properties (--ph-sa-*) set in variables.css.js.
 *
 * @returns {string} CSS string for standalone layout.
 */
export function layoutStandaloneCSS(style) {
    const widthValue = style.widthValue ?? 390;
    const widthUnit = style.widthUnit ?? 'px';
    const heightValue = style.heightUnit === 'auto' ? 'auto' : style.heightValue ?? 'auto';
    const heightUnit = style.heightUnit === 'auto' ? '' :  style.heightUnit ?? '';
    return `
    /* ===== Left background block ===== */
    .ph-bg--left {
        width: 50%;
        display: var(--ph-sa-show-left);
        position: relative;
    }

    .ph-bg--left::before {
        content: "";
        position: absolute;
        inset: 0;
        background-image: var(--ph-sa-bg-image);
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        filter: blur(var(--ph-sa-bg-blur));
        z-index: 0;
    }

    .ph-bg--left::after {
        content: "";
        position: absolute;
        inset: 0;
        background-color: var(--ph-sa-overlay-color);
        z-index: 1;
    }

    .ph-bg--left > * {
        position: relative;
        z-index: 2;
    }

    /* ===== Main background block ===== */
    .ph-bg--main {
        width: var(--ph-sa-bg-main-width);
        background-color: var(--ph-sa-bg-main-color);
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100svh;
        box-sizing: border-box;
        padding: 0 128px;
        position: relative;
    }

    .ph-bg--main::before {
        content: "";
        position: absolute;
        inset: 0;
        background-image: var(--ph-sa-bg-main-image);
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        filter: blur(var(--ph-sa-bg-blur));
        z-index: 0;
    }

    .ph-bg--main::after {
        content: "";
        position: absolute;
        inset: 0;
        background-color: var(--ph-sa-bg-main-overlay);
        z-index: 1;
    }

    .ph-bg--main > * {
        position: relative;
        z-index: 2;
    }

    /* ===== Right background block ===== */
    .ph-bg--right {
        width: 50%;
        display: var(--ph-sa-show-right);
        position: relative;
    }

    .ph-bg--right::before {
        content: "";
        position: absolute;
        inset: 0;
        background-image: var(--ph-sa-bg-image);
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        filter: blur(var(--ph-sa-bg-blur));
        z-index: 0;
    }

    .ph-bg--right::after {
        content: "";
        position: absolute;
        inset: 0;
        background-color: var(--ph-sa-overlay-color);
        z-index: 1;
    }

    .ph-bg--right > * {
        position: relative;
        z-index: 2;
    }

    /* ===== Outer box wrapper ===== */
    .ph-box-outer {
        width: ${widthValue}${widthUnit};
        max-width: 100%;
    }

    /* ===== Inner box: column layout ===== */
    .ph-box-inner {
        display: flex;
        flex-direction: column;
        align-items: var(--ph-sa-align-items);
        gap: 8px;
    }

    /* ===== Wrapper: flex container to center the survey card ===== */
    .ph-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
    }

    /* ===== Standalone survey box overrides ===== */
    .ph-survey {
        width: 100%;
        height: ${heightValue}${heightUnit};
        display: flex;
        flex-direction: column;
        --ph-options-max-height: 100%;
        background-color: var(--ph-bg-color);
        padding: 16px;
        border-radius: 8px;
        font-family: var(--ph-font-family);
        line-height: normal;
        box-sizing: border-box;
        background: var(--ph-bg-color);
        box-shadow: none !important;
        margin-top: 48px;
    }

    /* ===== Title typography ===== */
    .ph-title {
        font-size: 24px;
        font-weight: 600;
        font-family: var(--ph-font-family);
        color: var(--ph-sa-title-color);
        line-height: normal;
        display: var(--ph-sa-title-display);
        text-align: var(--ph-sa-text-align);
        overflow-wrap: break-word;
        word-break: break-word;
        max-width: 100%;
    }

    /* ===== Subtitle typography ===== */
    .ph-subtitle {
        font-family: var(--ph-font-family);
        color: var(--ph-sa-subtitle-color);
        display: var(--ph-sa-subtitle-display);
        text-align: var(--ph-sa-text-align);
        overflow-wrap: break-word;
        word-break: break-word;
        max-width: 100%;
    }

    /* ===== Extra button container ===== */
    .ph-extra-btn-container {
        justify-content: var(--ph-sa-extra-btn-justify);
        width: 100%;
        display: var(--ph-sa-extra-btn-display);
    }

    /* ===== Extra button ===== */
    .ph-extra-btn {
        background-color: var(--ph-sa-extra-btn-color);
        color: #ffffff;
        border: none;
        width: fit-content;
        max-width: 100%;
        margin-top: 3rem;
        padding: 12px 16px;
        font-size: 16px;
        cursor: pointer;
        border-radius: 4px;
        white-space: pre-wrap;
        word-break: break-word;
    }

    .ph-extra-btn:active,
    .ph-extra-btn:hover {
        opacity: 0.8;
    }

    /* ===== Branding link fix for standalone ===== */
    .ph-branding__text + a,
    .ph-branding__text + a > div {
        display: flex;
    }

    /* ===== Hide surrounding layout and background/overlays for Thank You message ===== */
    .ph-survey-host--thank-you .ph-title,
    .ph-survey-host--thank-you .ph-subtitle,
    .ph-survey-host--thank-you .ph-extra-btn-container {
        display: none !important;
    }

    .ph-survey-host--thank-you .ph-bg--left,
    .ph-survey-host--thank-you .ph-bg--right {
        display: none !important;
    }

    .ph-survey-host--thank-you .ph-bg--main {
        width: 100% !important;
        padding: 0 !important;
        background-color: transparent !important;
    }

    .ph-survey-host--thank-you .ph-bg--left::before,
    .ph-survey-host--thank-you .ph-bg--left::after,
    .ph-survey-host--thank-you .ph-bg--right::before,
    .ph-survey-host--thank-you .ph-bg--right::after,
    .ph-survey-host--thank-you .ph-bg--main::before,
    .ph-survey-host--thank-you .ph-bg--main::after {
        display: none !important;
        content: none !important;
    }

    .ph-survey-host--thank-you .ph-survey {
        margin-top: 0 !important;
    }

    /* ===== Standalone mobile responsive ===== */
    @media (max-width: 740px) {
        /* Host: switch from side-by-side to stacked layout */
        .ph-survey-host {
            flex-direction: column;
        }

        .ph-question__body {
            display: flex;
            flex-direction: column;
            flex: 1 1 100%;
        }

       

        /* Side image blocks: horizontal strip at top, 1/3 of screen */
        .ph-bg--left,
        .ph-bg--right {
            width: 100%;
            height: 33.33svh;
            flex-shrink: 0;
        }

        /* Right image: reorder above main content (DOM order is left→main→right) */
        .ph-bg--right {
            order: -1;
        }

        /* Main content: fill remaining height, enable scrolling */
        .ph-bg--main {
            width: 100%;
            height: auto;
            flex: 1 1 0%;
            min-height: 0;
            padding: 24px 20px;
            overflow-y: auto;
            align-items: flex-start;
            justify-content: flex-start;
        }

        /* Box outer: always full width on mobile */
        .ph-box-outer {
            width: 100%;
        }

        /* Survey card: overlay appearance with rounded corners */
        .ph-survey {
            width: 100%;
            margin-top: 16px;
            border-radius: 16px;
        }

        /* Title: slightly smaller for mobile */
        .ph-title {
            font-size: 20px;
        }

        /* Extra button: full-width tap target */
        .ph-extra-btn {
            width: 100%;
            margin-top: 1.5rem;
        }

        /* Wrapper: flex column for all questions, fills available space */
        .ph-wrapper {
            flex: 1 1 100%;
            display: flex;
            flex-direction: column;
            justify-content: start;
        }

        /* Engagement trigger: full height flex column wrapper */
        .ph-question--engagement-trigger.ph-survey {
            height: 100%;
            display: flex;
            flex-direction: column;
            flex: 1 1 100%;
            justify-content: start;
        }

        /* ----- Side image mode: survey fills full 2/3 area ----- */

        /* Main area matches survey background, fills remaining screen */
        .ph-survey-host--side-image .ph-bg--main {
            background-color: var(--ph-bg-color);
            padding: 24px 20px 0;
            display: flex;
            flex-direction: column;
        }

        /* Box outer fills main area as a flex column */
        .ph-survey-host--side-image .ph-box-outer {
            display: flex;
            flex-direction: column;
            flex: 1 1 0%;
            min-height: 0;
        }

        /* Survey blends into background — no card look */
        .ph-survey-host--side-image .ph-survey {
            background: transparent;
            border-radius: 0;
            margin-top: 16px;
            padding: 0;
            box-shadow: none !important;
            height: 100%;
            display: flex;
            flex-direction: column;
        }

        .ph-survey-host--side-image .ph-options,
        .ph-survey-host--side-image .ph-matrix {
            max-height: 100%;
        }

        /* Branding inside survey pushed to bottom */
        .ph-survey-host--side-image .ph-branding {
            margin-top: 10px;
            padding-bottom: 16px;
        }

        /* Extra button container at bottom */
        .ph-survey-host--side-image .ph-extra-btn-container {
            margin-top: auto;
            padding-bottom: 20px;
        }

        .ph-survey-host--full-image .ph-bg--main {
            padding: 0;
            justify-content: flex-end;
            align-items: end;
        }

        .ph-survey-host--full-image .ph-box-inner {
            padding: 0 16px;
        }

        .ph-survey-host--full-image .ph-extra-btn-container {
            padding: 0 16px;
            box-sizing: border-box;
            margin-bottom: 32px;
        }

        .ph-survey-host--full-image .ph-survey {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0; 
        }

        .ph-box-outer {
            display: flex;
            flex-direction: column;
        }

        .ph-box-inner {
            order: 1;
         }

        .ph-extra-btn-container {
            order: 2;
         }

        .ph-wrapper {
            order: 3;
         }

    }`;
}
